import { getAccessToken } from '@repo/api-client';
import { Button } from '@repo/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/card';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useLoginMutation } from '../queries/auth';

interface LoginFormValues {
  email: string;
  password: string;
}

// Reject anything that could escape the current origin. A protocol-relative URL
// like `//evil.example/path` still satisfies `startsWith('/')` but the browser
// resolves it against the current scheme, so it is an open-redirect vector.
function safeInternalRedirect(value: string | undefined): string {
  if (!value?.startsWith('/') || value.startsWith('//')) return '/';
  return value;
}

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>) => ({
    redirect: typeof s.redirect === 'string' ? s.redirect : undefined,
  }),
  beforeLoad: () => {
    if (getAccessToken()) {
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const search = useSearch({ from: '/login' });
  const navigate = useNavigate();
  const loginMutation = useLoginMutation();

  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>();

  const onSubmit = async (values: LoginFormValues) => {
    try {
      await loginMutation.mutateAsync({ email: values.email, password: values.password });
      const destination = safeInternalRedirect(search.redirect);
      navigate({ to: destination });
    } catch (err: unknown) {
      const status =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'status' in err.response
          ? (err.response as { status: number }).status
          : undefined;

      if (status === 401) {
        toast.error('Invalid email or password.');
      } else if (status === 403) {
        toast.error('Not authorized as a curator. Contact your admin.');
      } else {
        toast.error('Login failed. Please try again.');
      }
      resetField('password');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <Card className="w-full max-w-sm border-rule bg-paper-panel shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-display font-bold text-[17px] text-red tracking-wide">
              প্রশ্নমাস্টার
            </span>
            <span className="font-mono text-[10px] text-ink-mute font-normal">ADMIN</span>
          </div>
          <CardTitle className="text-[15px] font-semibold text-ink">Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email" className="text-[13px] text-ink-dim">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                className="h-9 text-[13px] bg-paper border-rule text-ink placeholder:text-ink-mute focus-visible:ring-blue"
                {...register('email', { required: 'Email is required' })}
              />
              {errors.email && <p className="text-[12px] text-red-500">{errors.email.message}</p>}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password" className="text-[13px] text-ink-dim">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                className="h-9 text-[13px] bg-paper border-rule text-ink placeholder:text-ink-mute focus-visible:ring-blue"
                {...register('password', { required: 'Password is required' })}
              />
              {errors.password && (
                <p className="text-[12px] text-red-500">{errors.password.message}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || loginMutation.isPending}
              className="h-9 text-[13px] mt-1"
            >
              {loginMutation.isPending ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p className="mt-5 text-[12px] text-ink-mute text-center">
            Forgot password? — contact your admin.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
